import { Fragment, memo } from 'react';
import { Link, To } from 'react-router-dom';

import styled from 'styled-components';

import { Avatar, AvatarProps } from '@components/avatar';
import { IconChevronRightSmall } from '@components/icons/chevron-right-small';
import { LabelInputText, LabelInputTextProps } from '@components/label-input-text';
import { Stack } from '@components/stack';
import { lineClamp } from '@styles/layout-helpers';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';
import { Rename, XOR } from '@utils/types';

type TBreadcrumbItemXor = XOR<
  Rename<
    Pick<LabelInputTextProps, 'extraMenuItems' | 'onSave' | 'onIsEditingChange'>,
    { onSave: 'onRename' }
  >,
  {}
>;
type TBreadcrumbItem = TBreadcrumbItemXor & {
  label: string;
  link?: To;
  avatar?: AvatarProps;
};

interface BreadcrumbItemProps {
  item: TBreadcrumbItem;
  isCurrent: boolean;
}

const BreadcrumbItem = memo(({ item, isCurrent }: BreadcrumbItemProps) => {
  return (
    <Item
      className="breadcrumb-item"
      data-active={isCurrent}
      aria-current={isCurrent ? 'page' : undefined}
    >
      <Stack space={1} orientation="horizontal">
        {item.avatar && <Avatar {...item.avatar} />}
        {item.onRename ? (
          <LabelInputText
            defaultValue={item.label}
            onSave={item.onRename}
            onIsEditingChange={item.onIsEditingChange}
            extraMenuItems={item.extraMenuItems}
            font="bold18"
            hideLabel
          />
        ) : (
          <span className="item-label">
            {item.link && !isCurrent ? <Link to={item.link}>{item.label}</Link> : item.label}
          </span>
        )}
      </Stack>
    </Item>
  );
});

const Item = styled.li({
  font: theme.font.bold18,
  color: theme.color.textHelper,

  '&[data-active="true"]': {
    color: theme.color.textPrimary,
  },

  a: {
    transition: transitionStandard('color'),
  },
  '&[data-active="false"]:hover a': {
    color: theme.color.elementBlue100,
  },

  '&:has(:focus-visible) a': {
    outline: 'none',
    color: theme.color.elementBlue100,
    borderBottom: `${pxToRem(2)} solid ${theme.color.elementBlue100}`,
  },

  '.item-label': {
    ...lineClamp('-webkit-inline-box', 1, true),
  },
});

export interface BreadcrumbProps {
  items: Array<TBreadcrumbItem>;
}

export const Breadcrumb = memo(({ items }: BreadcrumbProps) => {
  return (
    <Crumbs className="breadcrumbs" aria-label="Breadcrumb">
      {items.map((item, index) => {
        const isLastItem = index === items.length - 1;
        return (
          <Fragment key={item.label}>
            <BreadcrumbItem item={item} isCurrent={isLastItem} />
            {!isLastItem && <IconChevronRightSmall color="uiTrans20" />}
          </Fragment>
        );
      })}
    </Crumbs>
  );
});

const Crumbs = styled.ol({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(4),
});
